import IconeAjuda from '@/global/components/IconeAjuda'
import { Divider, Grid, Box, MenuItem } from '@mui/material'
import { FormField, FormFieldCheckbox } from 'global/components/Form'
import { Link } from 'global/components/Link'
import { hasPermission } from 'hooks/useBackendMenu'
import { ExternalLinkIcon } from 'lucide-react'
import { useFormContext } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { regrasCalculo, tabelaHorarios } from 'service/api'
import { useStore } from 'store'
import AutocompleteFuncao from './AutocompleteFuncao'

export function ParametrosDoCalculo({ editar }: { editar?: boolean }) {
    const { empresaId, id } = useParams()
    const cliente = useStore(state => state.customer)
    const empresaIdStore = useStore(state => state.companyId)
    const { watch } = useFormContext()
    const tabelaHorarioId = watch('tabelaHorarioId')

    const { data: tabelaHorariosData } = tabelaHorarios.useFetch({
        queryParams: id ? { clienteId: id } : {},
        enabled: !!id || !!cliente,
    })

    const { data: rgCalculoData } = regrasCalculo.useFetch({
        queryParams: id ? { clienteId: id } : {},
        enabled: !!empresaId || !!empresaIdStore,
    })

    return (
        <section id="parametros">
            <Divider sx={{ marginY: 2 }} textAlign="center">
                Parâmetros do cálculo
            </Divider>
            <Grid
                container
                columns={24}
                spacing={1}
                columnSpacing={2}
                alignItems="self-start"
            >
                <Grid item xs={8}>
                    <FormField
                        name="matricula"
                        label="Matrícula"
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ maxlength: 10 }}
                        placeholder={
                            editar ? undefined : '(Gerar automaticamente)'
                        }
                        required={editar}
                        type="number"
                        sx={{
                            'input::-webkit-outer-spin-button, input::-webkit-inner-spin-button':
                                {
                                    '-webkit-appearance': 'none',
                                    margin: 0,
                                },
                            'input[type=number]': {
                                '-moz-appearance': 'textfield',
                            },
                        }}
                    />
                </Grid>
                <Grid item xs={8}>
                    <FormField
                        name="dtAdmissao"
                        InputLabelProps={{ shrink: true }}
                        label="Data de admissão"
                        type="date"
                        required
                    />
                </Grid>
                <Grid item xs={8}>
                    <FormField
                        name="dtRescisao"
                        InputLabelProps={{ shrink: true }}
                        label="Data de rescisão"
                        type="date"
                    />
                </Grid>
                <Grid item xs={12}>
                    <FormField
                        required
                        name="regraCalculo"
                        label="Regra de cálculo"
                        select
                        onKeyDownCapture={e => e.stopPropagation()}
                    >
                        {hasPermission(['create', 'RegrasDeCálculo'], () => (
                            <AdicionarNova href="/cadastros/regras-calculo/adicionar" />
                        ))}
                        {rgCalculoData?.map(regra => (
                            <MenuItem key={regra.id} value={+regra.id}>
                                {regra.nome}
                            </MenuItem>
                        ))}
                    </FormField>
                </Grid>
                <Grid item xs={12}>
                    <FormField
                        name="tabelaHorarioId"
                        label={'Tabela de horário'}
                        select
                        onKeyDownCapture={e => e.stopPropagation()}
                        {...(editar && {
                            disabled: true,
                            label: tabelaHorarioId ? (
                                <Link
                                    href={`/cadastros/tabela-horarios/${tabelaHorarioId}/editar`}
                                    className="inline-flex items-center gap-2"
                                >
                                    Tabela de horário atual{' '}
                                    <ExternalLinkIcon className="size-4" />
                                </Link>
                            ) : (
                                'Tabela de horário'
                            ),
                        })}
                    >
                        {hasPermission(['create', 'TabelaDeHorários'], () => (
                            <AdicionarNova href="/cadastros/tabela-horarios/adicionar" />
                        ))}
                        <MenuItem value="">Nenhuma (Horista)</MenuItem>
                        {tabelaHorariosData?.map((tabelaHorario, index) => (
                            <MenuItem key={index} value={+tabelaHorario.id}>
                                {tabelaHorario.nome}
                            </MenuItem>
                        ))}
                    </FormField>
                </Grid>
                <Grid item xs={12}>
                    <AutocompleteFuncao />
                </Grid>
                <Grid item xs={9} mt={-1}>
                    <Box display="flex" alignItems="center">
                        <FormFieldCheckbox
                            name="horarioNoturno"
                            label="Horário noturno"
                        />
                        <IconeAjuda>
                            Marque quando a jornada do funcionário avança sobre
                            a madrugada.
                            <br />
                            <br />O cálculo passa a tratar as marcações após a
                            meia-noite como continuação da mesma jornada — e não
                            como um novo dia — e separa as horas trabalhadas no
                            período noturno para a apuração do adicional
                            noturno.
                        </IconeAjuda>
                    </Box>
                </Grid>
                <Grid item xs={10} mt={-1}>
                    <Box display="flex" alignItems="center">
                        <FormFieldCheckbox
                            name="desconsiderarFeriados"
                            label="Desconsiderar feriados"
                        />
                        <IconeAjuda>
                            Marque quando o funcionário deve trabalhar
                            normalmente nos feriados.
                            <br />
                            <br />
                            Os feriados da cidade de trabalho deixam de ser
                            aplicados a ele: o dia é apurado como dia comum, sem
                            folga prevista e sem as horas extras de feriado.
                        </IconeAjuda>
                    </Box>
                </Grid>
            </Grid>
            <div className="h-16" />
        </section>
    )
}

function AdicionarNova({ href }: { href: string }) {
    const { watch } = useFormContext()
    const navigate = useNavigate()

    return (
        <MenuItem
            value="0"
            onClick={() => {
                localStorage.setItem(
                    '@rascunho:funcionario',
                    JSON.stringify(watch()),
                )
                navigate(href)
            }}
        >
            Adicionar Nova
        </MenuItem>
    )
}
